import React from 'react'

// Cards
import {
    SectionHeader,
    Props as SectionHeaderProps,
} from 'component-library/section-header/SectionHeader/SectionHeader'
import {
    StyledLima,
    StyledHeader,
    StyledGridItem,
} from 'component-library/collections-new/Lima/Lima.styled'
import { Portrait, CardOrientationOptions } from 'component-library/cards/Portrait/Portrait'
import { getCardItemProps, LayoutName } from 'component-library/cards/Portrait/Portrait.layouts'
import { CardSwitcher } from 'component-library/__helpers/CardSwitcher'
import { ImageWidths } from 'common/helpers/image'

// Themes, Metrics, Events
import { Product } from 'component-library/__product'
import { FontScales } from 'component-library/__styling/settings/fontScale'

import { ImpressionAvailable } from 'component-library/__helpers/impression-available-helper'
import {
    LandscapeLayout,
    getLandscapeCardItemProps,
} from 'component-library/cards/Landscape/Landscape.layouts'
import { LandscapeStackedLayout } from 'component-library/cards/LandscapeStacked/LandscapeStacked.layouts'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { invertMaybeLoadedItems } from '../helpers/loading'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { calculateLayoutsLength, renderCardLayout } from './helpers/layouts'
import { MediaMode } from 'component-library/cards/CardMedia/CardMedia'
import { useProduct } from 'common/hooks/useProduct'
import { CollectionEvent, createCollectionAvailableEvent } from 'common/events/collection-events'
import { CardItem } from 'common/client-data-types/card-types'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { Landscape } from 'component-library/cards/Landscape/Landscape'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export type ListLength = 1 | 2 | 3 | 4 | 5 | 6

export interface NovemberLayout {
    type: 'november'
    listLength: ListLength
    timestamp?: TimestampType
    sectionHeader?: SectionHeaderProps
    noStretch?: boolean
}

export interface LimaHeroLayout {
    type: 'hero'
    mediaMode?: MediaMode
}

export type LimaLayout = (LayoutName | LandscapeLayout | LandscapeStackedLayout)[] | NovemberLayout

export type LimaLayouts =
    | [LimaLayout, LimaLayout, LimaLayout, LimaLayout]
    | [LimaLayout, LimaLayout, LimaLayout]
    | [LimaLayout, LimaLayout]
    | [LimaLayout]
    | LimaHeroLayout

export type RemainingLayouts = LayoutName | LandscapeLayout

export interface LimaProps {
    className?: string
    hideKicker?: boolean
    hideByline?: boolean
    hideTeaser?: boolean
    hideText?: boolean
    timestamp?: TimestampType
    sectionHeader?: SectionHeaderProps
    hasBackground?: boolean
    fixedRatios?: FixedRatio[] | CardBreakpointRatios
    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews']
    onEvent: (event: CollectionEvent) => void
    initialColumns?: number
    intermediateColumns?: number
    finalColumns?: number
    fontScale?: FontScales
    textVerticalSpacing?: keyof ThemeMargins
    verticalSpacing?: keyof ThemeMargins | undefined
    disableImageLazyLoad?: boolean
    isSponsoredCollection?: boolean
    cardOrientation?: CardOrientationOptions
    cardLayout?: LimaLayouts
    remainingCardLayout?: RemainingLayouts
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
    stretchSelf?: boolean
    hasSeparator?: boolean
}

export interface CardOverrideOptions {
    fontScale: FontScales
    hideText: boolean
    hideKicker: boolean
    hideByline?: boolean
    isSponsoredCollection?: boolean
    hideTeaser?: boolean
    disableImageLazyLoad?: boolean
}

export const Lima: React.FC<LimaProps> = props => {
    const {
        className,
        sectionHeader,
        hasBackground,
        onEvent,
        initialColumns = 1,
        intermediateColumns = 4,
        finalColumns = 4,
        textVerticalSpacing,
        verticalSpacing,
        cardOrientation,
        cardLayout,
        remainingCardLayout = 'common',
        hideTeaser,
        hideText = false,
        hideKicker = false,
        fontScale,
        isSponsoredCollection,
        disableImageLazyLoad,
        expectedCards,
        stretchSelf,
    } = props

    const product = useProduct()
    const cardOptionOverrides: CardOverrideOptions = {
        fontScale: fontScale || 1,
        hideText,
        hideKicker,
        hideByline: product === 'sevennews' || props.hideByline,
        isSponsoredCollection,
        hideTeaser: product === 'sevennews' ? true : hideTeaser,
        disableImageLazyLoad: disableImageLazyLoad,
    }

    /**
     * This determines the initial value of the card width, which also affects the srcSet generated etc
     * so for Lima collections with items < 2 a larger image width will be served
     */
    const getImageWidth = (itemsCount: number, product: Product) => {
        switch (itemsCount) {
            case 1:
            case 2:
                switch (product) {
                    case Product.TheWest:
                        return 500
                    case Product.SevenNews:
                        return 650
                }
                return 650
        }
        switch (product) {
            case Product.TheWest:
                return 268
            case Product.SevenNews:
                return 320
        }
        return 320
    }

    const items = invertMaybeLoadedItems(props.items, expectedCards)

    const remainingItems = () => {
        return cardLayout !== undefined
            ? items.slice(items.length - (items.length - calculateLayoutsLength(cardLayout)))
            : items.slice(0, items.length)
    }

    const hasItems = items.length >= 1

    return (
        <React.Fragment>
            {sectionHeader && hasItems && (
                <StyledHeader>
                    <SectionHeader {...sectionHeader} canGrow={true} />
                </StyledHeader>
            )}
            <ImpressionAvailable
                loading={!props.items.loaded}
                available={() => {
                    if (!props.items.loaded) {
                        console.warn('Available should never be called when loading is true')
                        return
                    }
                    props.onEvent(createCollectionAvailableEvent(props.items.result, 'Lima'))
                }}
            >
                {ref => (
                    <StyledLima
                        ref={ref}
                        className={className}
                        initialColumns={initialColumns}
                        finalColumns={finalColumns}
                        verticalSpacing={verticalSpacing}
                        stretchSelf={stretchSelf}
                    >
                        {cardLayout &&
                            renderCardLayout(
                                cardLayout,
                                cardOptionOverrides,
                                props,
                                items,
                                getImageWidth,
                                product
                            )}

                        {remainingItems().map((item, index) => {
                            const numberOfItems = cardLayout
                                ? remainingItems().length
                                : items.length

                            // if the above renders a card then we should increment by 2, as the above
                            // card will be 1
                            const cardNumber = cardLayout ? index + 2 : index + 1

                            return (
                                <StyledGridItem
                                    key={index}
                                    numberOfItems={numberOfItems}
                                    intermediateColumns={intermediateColumns}
                                    initialColumns={initialColumns}
                                    finalColumns={finalColumns}
                                >
                                    {typeof remainingCardLayout === 'object' ? (
                                        item && (
                                            <CardSwitcher
                                                key={index}
                                                item={item}
                                                cardContext="lima-remaining"
                                                cardNumber={cardNumber}
                                                publicationCard={publicationItem => (
                                                    <Landscape
                                                        key={index}
                                                        disableImageLazyLoad={disableImageLazyLoad}
                                                        timestamp={props.timestamp}
                                                        {...getLandscapeCardItemProps(
                                                            publicationItem,
                                                            remainingCardLayout,
                                                            {
                                                                onEvent,
                                                            },
                                                            cardNumber
                                                        )}
                                                    />
                                                )}
                                            />
                                        )
                                    ) : (
                                        <CardSwitcher
                                            key={index}
                                            item={item}
                                            cardContext="lima-remaining"
                                            cardNumber={cardNumber}
                                            publicationCard={publicationItem => (
                                                <Portrait
                                                    data-layout={remainingCardLayout}
                                                    imageWidths={getImageWidth(
                                                        items.length,
                                                        product
                                                    )}
                                                    textVerticalSpacing={textVerticalSpacing}
                                                    hasBackground={hasBackground}
                                                    fixedRatio={props.fixedRatios}
                                                    {...getCardItemProps(
                                                        publicationItem,
                                                        remainingCardLayout,
                                                        {
                                                            onEvent,
                                                        },
                                                        cardNumber,
                                                        cardOrientation
                                                    )}
                                                    {...cardOptionOverrides}
                                                />
                                            )}
                                        />
                                    )}
                                </StyledGridItem>
                            )
                        })}
                    </StyledLima>
                )}
            </ImpressionAvailable>
        </React.Fragment>
    )
}
Lima.displayName = 'Lima'
